import { apiSlice } from './apiSlice'

/**
 * Virtual Warehouse RTK Query slice — mirrors `saleDeliveryOrderApi` (injectEndpoints
 * on the shared `apiSlice`). Two datasheets:
 *   - Received (frm Virtual Warehouse Received)
 *   - Quality Discount (frm Virtual Warehouse Quality Discount)
 *
 * Base path: `/virtual-warehouse`. All list endpoints provide the single
 * `VirtualWarehouse` tag; every mutation invalidates it.
 */

/** Received datasheet row (camelCase = column keys, order 0..41). */
export interface ReceivedRow {
  id: number
  rdnNo: string | null
  dateIn: string | null
  doNo: string | null
  truckNo: string | null
  fromSupplier: string | null
  supplierName: string | null
  toClient: string | null
  clientName: string | null
  warehouseCode: string | null
  warehouseName: string | null
  quality: string | null
  announcedQuantity: number | null
  noOfBag: number | null
  vgmQuantity: number | null
  mc: number | null
  bb: number | null
  brown: number | null
  cherry: number | null
  overdry: number | null
  fm: number | null
  black: number | null
  brokens: number | null
  foreign: number | null
  insect: number | null
  immature: number | null
  old: number | null
  mouldy: number | null
  white: number | null
  s18: number | null
  s17: number | null
  s16: number | null
  s15: number | null
  s14: number | null
  s13: number | null
  s12: number | null
  underS12: number | null
  deductionKg: number | null
  deductionVnd: number | null
  paymentQuantity: number | null
  status: string | null
  note: string | null
  deductionOther: number | null
}

/** Quality Discount datasheet row (camelCase). */
export interface QualityDiscountRow {
  id: number
  warehouse: string | null
  shortName: string | null
  qualityCode: string | null
  mcDiscountRate: number | null
  bbDiscountRate: number | null
  blackDiscountRate: number | null
  brokensDiscountRate: number | null
  fmDiscountRate: number | null
  foreignDiscountRate: number | null
  mouldyDiscountRate: number | null
  brownDiscountRate: number | null
  insectDiscountRate: number | null
  cherryDiscountRate: number | null
  whiteDiscountRate: number | null
  overdryDiscountRate: number | null
  immatureDiscountRate: number | null
  oldDiscountRate: number | null
  s18DiscountRate: number | null
  s17DiscountRate: number | null
  s16DiscountRate: number | null
  s15DiscountRate: number | null
  s14DiscountRate: number | null
  s13DiscountRate: number | null
  s12DiscountRate: number | null
  underS12DiscountRate: number | null
  mcPermit: number | null
  bbPermit: number | null
  blackPermit: number | null
  brokensPermit: number | null
  fmPermit: number | null
  foreignPermit: number | null
  mouldyPermit: number | null
  brownPermit: number | null
  insectPermit: number | null
  cherryPermit: number | null
  whitePermit: number | null
  overdryPermit: number | null
  immaturePermit: number | null
  oldPermit: number | null
  s18Permit: number | null
  s17Permit: number | null
  s16Permit: number | null
  s15Permit: number | null
  s14Permit: number | null
  s13Permit: number | null
  s12Permit: number | null
  underS12Permit: number | null
}

export interface VwListResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface VwListQuery {
  page: number
  pageSize: number
  q?: string
  /** Received-only: server-side "Date in" range (from the shared page filter). */
  dateInFrom?: string | null
  dateInTo?: string | null
}

export interface ReceivedExportQuery {
  dateInFrom?: string | null
  dateInTo?: string | null
}

/**
 * Shared "page filter" block (mirrors BE virtual_warehouse/page_filter baseline).
 * Only the Received tab uses date_in; persisted per-user via usePreference.
 */
export interface VwPageFilter {
  dateInFrom: string | null
  dateInTo: string | null
}

/** Combo source item (lookups). */
export interface LookupItem {
  value: string
  label: string
  meta?: Record<string, unknown>
}
export interface LookupResponse {
  items: LookupItem[]
  total: number
}

/** New/Edit/Copy payloads — camelCase, all fields optional. */
export type ReceivedWrite = Partial<Omit<ReceivedRow, 'id'>>
export type QualityDiscountWrite = Partial<Omit<QualityDiscountRow, 'id'>>

export interface SuggestReceiptNoResponse {
  receiptNo: string
  exists: boolean
}
export interface CheckReceiptNoResponse {
  receiptNo: string
  exists: boolean
}
/** DO-derived autofill for the Received form. */
export interface DoDerivedResponse {
  saleContract: string | null
  packaging: string | null
  quality: string | null
  netWeightKg: number | null
}
export interface PrintPreviewResponse {
  title: string
  html: string
  message?: string
}

const virtualWarehouseApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Received list ─────────────────────────────────────────────────────
    getReceivedList: builder.query<VwListResponse<ReceivedRow>, VwListQuery>({
      query: ({ page, pageSize, q, dateInFrom, dateInTo }) => ({
        url: '/virtual-warehouse/received',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(dateInFrom ? { dateInFrom } : {}),
          ...(dateInTo ? { dateInTo } : {}),
        },
      }),
      providesTags: ['VirtualWarehouse'],
    }),
    createReceived: builder.mutation<{ id: number }, ReceivedWrite>({
      query: (body) => ({ url: '/virtual-warehouse/received', method: 'POST', body }),
      invalidatesTags: ['VirtualWarehouse'],
    }),
    updateReceived: builder.mutation<{ id: number }, { id: number; body: ReceivedWrite }>({
      query: ({ id, body }) => ({ url: `/virtual-warehouse/received/${id}`, method: 'PUT', body }),
      invalidatesTags: ['VirtualWarehouse'],
    }),
    deleteReceived: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/virtual-warehouse/received/${id}`, method: 'DELETE' }),
      invalidatesTags: ['VirtualWarehouse'],
    }),

    // ── Received — Suggest / Check / DO-derived (access-suggest pattern) ────
    getReceivedSuggestReceiptNo: builder.query<SuggestReceiptNoResponse, void>({
      query: () => '/virtual-warehouse/received/suggest-receipt-no',
      keepUnusedDataFor: 0,
    }),
    getReceivedCheckReceiptNo: builder.query<CheckReceiptNoResponse, string>({
      query: (receiptNo) => ({
        url: '/virtual-warehouse/received/check-receipt-no',
        params: { receiptNo },
      }),
      keepUnusedDataFor: 0,
    }),
    getReceivedDoDerived: builder.query<DoDerivedResponse, string>({
      query: (doNo) => ({ url: '/virtual-warehouse/received/do-derived', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),

    // ── Received — Print previews ──────────────────────────────────────────
    getDnPrintPreview: builder.query<PrintPreviewResponse, string>({
      query: (receiptNo) => ({ url: '/virtual-warehouse/received/dn-print/preview', params: { receiptNo } }),
      keepUnusedDataFor: 0,
    }),
    getRnPrintPreview: builder.query<PrintPreviewResponse, string>({
      query: (receiptNo) => ({ url: '/virtual-warehouse/received/rn-print/preview', params: { receiptNo } }),
      keepUnusedDataFor: 0,
    }),

    // ── Received — Export (xlsx blob) ──────────────────────────────────────
    exportReceived: builder.query<Blob, ReceivedExportQuery | void>({
      query: (arg) => ({
        url: '/virtual-warehouse/received/export',
        params: {
          ...(arg?.dateInFrom ? { dateInFrom: arg.dateInFrom } : {}),
          ...(arg?.dateInTo ? { dateInTo: arg.dateInTo } : {}),
        },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
      keepUnusedDataFor: 0,
    }),

    // ── Quality Discount list ─────────────────────────────────────────────
    getQualityDiscountList: builder.query<VwListResponse<QualityDiscountRow>, VwListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/virtual-warehouse/quality-discount',
        params: { page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['VirtualWarehouse'],
    }),
    createQualityDiscount: builder.mutation<{ id: number }, QualityDiscountWrite>({
      query: (body) => ({ url: '/virtual-warehouse/quality-discount', method: 'POST', body }),
      invalidatesTags: ['VirtualWarehouse'],
    }),
    updateQualityDiscount: builder.mutation<{ id: number }, { id: number; body: QualityDiscountWrite }>({
      query: ({ id, body }) => ({ url: `/virtual-warehouse/quality-discount/${id}`, method: 'PUT', body }),
      invalidatesTags: ['VirtualWarehouse'],
    }),
    deleteQualityDiscount: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/virtual-warehouse/quality-discount/${id}`, method: 'DELETE' }),
      invalidatesTags: ['VirtualWarehouse'],
    }),

    // ── Lookups (combo sources) ───────────────────────────────────────────
    vwCpLookup: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/virtual-warehouse/lookups/cp', params: q ? { q } : {} }),
    }),
    // CODE-first label ("CODE — Name") — Quality Discount warehouse combo
    // (Access VirtualFactoryQryCP, ColumnWidths "720;10080" shows code first).
    vwCpCodeFirstLookup: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/virtual-warehouse/lookups/cp', params: { code_first: true, ...(q ? { q } : {}) } }),
    }),
    vwDoNoLookup: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/virtual-warehouse/lookups/do-no', params: q ? { q } : {} }),
    }),
    vwWarehouseLookup: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/virtual-warehouse/lookups/warehouse', params: q ? { q } : {} }),
    }),
    vwQualityLookup: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/virtual-warehouse/lookups/quality', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetReceivedListQuery,
  useCreateReceivedMutation,
  useUpdateReceivedMutation,
  useDeleteReceivedMutation,
  useLazyGetReceivedSuggestReceiptNoQuery,
  useLazyGetReceivedCheckReceiptNoQuery,
  useLazyGetReceivedDoDerivedQuery,
  useLazyGetDnPrintPreviewQuery,
  useLazyGetRnPrintPreviewQuery,
  useLazyExportReceivedQuery,
  useGetQualityDiscountListQuery,
  useCreateQualityDiscountMutation,
  useUpdateQualityDiscountMutation,
  useDeleteQualityDiscountMutation,
  useVwCpLookupQuery,
  useLazyVwCpLookupQuery,
  useVwCpCodeFirstLookupQuery,
  useLazyVwCpCodeFirstLookupQuery,
  useVwDoNoLookupQuery,
  useLazyVwDoNoLookupQuery,
  useVwWarehouseLookupQuery,
  useLazyVwWarehouseLookupQuery,
  useVwQualityLookupQuery,
  useLazyVwQualityLookupQuery,
} = virtualWarehouseApi

export default virtualWarehouseApi
